export function WhenRemodelMakesSense() {
  const scenarios = [
    {
      icon: 'ri-arrow-up-line',
      title: 'Strong Rent Growth Market',
      description: 'Your area is experiencing rapid rent increases (10%+ annually) and you can capture significantly higher rents that justify renovation costs.',
      requirements: [
        'Market rents 30%+ above current rents',
        'Low vacancy rates (&lt;5%)',
        'Strong job growth and population influx',
        'Limited competing supply'
      ]
    },
    {
      icon: 'ri-time-line',
      title: 'Long-Term Hold Strategy',
      description: 'You plan to keep the property for 5-10+ years and can amortize renovation costs over a long holding period while enjoying increased cash flow.',
      requirements: [
        'No immediate need for liquidity',
        'Strong personal cash reserves',
        'Ability to weather market cycles',
        'Experience managing renovations'
      ]
    },
    {
      icon: 'ri-funds-line',
      title: 'Access to Cheap Capital',
      description: 'You have access to low-interest financing or cash reserves that make the cost of capital minimal compared to potential returns.',
      requirements: [
        'Interest rates below 4%',
        'No need to liquidate other investments',
        'Strong credit and borrowing capacity',
        'Renovation costs &lt;20% of property value'
      ]
    },
    {
      icon: 'ri-building-2-line',
      title: 'Minimal Renovation Needed',
      description: 'Only cosmetic updates required (paint, flooring, fixtures) that cost less than $5,000 per unit and can increase rents by 15%+.',
      requirements: [
        'Good bones and systems',
        'No major structural issues',
        'Quick 30-60 day timeline',
        'Proven rent comps in area'
      ]
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            When Does Remodeling Actually Make Sense?
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            We're not saying renovations never work. Here are the specific scenarios where remodeling before selling might be the right choice.
          </p>
        </div>

        {/* Scenarios Grid */}
        <div className="grid md:grid-cols-2 gap-8 mb-16">
          {scenarios.map((scenario, index) => (
            <div key={index} className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border-2 border-gray-200">
              <div className="w-14 h-14 bg-gray-700 rounded-xl flex items-center justify-center mb-6">
                <i className={`${scenario.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-2xl font-bold mb-4">{scenario.title}</h3>
              <p className="text-gray-700 mb-6 leading-relaxed">{scenario.description}</p>
              <div className="bg-white rounded-xl p-6 border border-gray-200">
                <div className="font-bold mb-3 text-gray-800">Required Conditions:</div>
                <ul className="space-y-2">
                  {scenario.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <i className="ri-checkbox-circle-line text-lg text-green-600 mt-0.5 flex-shrink-0"></i>
                      <span className="text-gray-700" dangerouslySetInnerHTML={{ __html: req }}></span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Reality Check */}
        <div className="bg-gradient-to-br from-red-50 to-white rounded-2xl p-10 border-2 border-red-200 mb-12">
          <h3 className="text-3xl font-bold mb-6 text-center">The Honest Reality Check</h3>
          <div className="max-w-4xl mx-auto">
            <p className="text-xl text-gray-700 mb-6 leading-relaxed">
              If you're reading this page, chances are your situation doesn't fit these ideal scenarios. Most apartment building owners considering a sale face one or more of these challenges:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="flex items-start gap-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700">Deferred maintenance has piled up over years</span>
              </div>
              <div className="flex items-start gap-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700">Limited cash reserves for major renovations</span>
              </div>
              <div className="flex items-start gap-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700">Need to sell quickly for personal reasons</span>
              </div>
              <div className="flex items-start gap-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700">Tired of dealing with property management</span>
              </div>
              <div className="flex items-start gap-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700">Market rents won't support renovation costs</span>
              </div>
              <div className="flex items-start gap-4">
                <i className="ri-close-circle-line text-2xl text-red-600 mt-1 flex-shrink-0"></i>
                <span className="text-gray-700">No experience managing construction projects</span>
              </div>
            </div>
            <p className="text-xl text-gray-800 font-bold mt-8 text-center">
              If any of these apply to you, selling AS IS is almost certainly your best option.
            </p>
          </div>
        </div>

        {/* Image */}
        <div className="rounded-2xl overflow-hidden shadow-xl">
          <img
            src="https://public.readdy.ai/ai/img_res/2bb90a7cf131ba93bacfe7484b6b11fd.jpg"
            alt="Deciding between remodeling or selling as-is"
            className="w-full h-[500px] object-cover object-top"
          />
        </div>
      </div>
    </section>
  );
}